/**
 * What clients read about each stage of their matter, in the firm's own words.
 *
 * Each stage has two pieces of client wording: a short TITLE, which is the step's name
 * in the progress tracker on the client's portal and app, and a SENTENCE, the status
 * line they read while their matter sits at that stage. Both are editable here, and
 * nothing else is — the stage's internal name, its order and the moves allowed between
 * stages are the lifecycle's shape, which the backend keeps read-only.
 *
 * The preview on the right is built by the same rule the server uses for the real
 * tracker (`domains.cases.queries.client_progress`): pauses (on hold, waiting for the
 * client) and endings (closed, cancelled) are not steps, and reaching the last step
 * shows every step done.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type WorkflowDetail,
  type WorkflowStageWording,
  type WorkflowSummary,
} from '@legalcrm/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Form,
  FormActions,
  FormInput,
  FormTextarea,
  Modal,
  SkeletonText,
  Tabs,
  cn,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { Can } from '@/auth/permissions';

const TITLE_MAX = 60;
const SENTENCE_MAX = 280;

/** Stage types that are not steps on the way. Mirrors `_NOT_A_STEP` on the server. */
const NOT_A_STEP = new Set(['blocked', 'terminal_cancelled', 'terminal_success']);

const schema = z.object({
  client_title: z
    .string()
    .trim()
    .min(1, 'Give this step a short title')
    .max(TITLE_MAX, `Keep it to ${TITLE_MAX} characters so it fits on a phone`),
  client_label: z
    .string()
    .trim()
    .min(1, 'Write a sentence for the client')
    .max(SENTENCE_MAX, `Keep it to ${SENTENCE_MAX} characters`),
});

type Values = z.infer<typeof schema>;

type StepState = 'done' | 'current' | 'upcoming';

export function ClientProgressPage() {
  const workflows = useApiQuery<{ results: WorkflowSummary[] } | WorkflowSummary[]>(
    qk.list('workflows'),
    STAFF.workflows,
  );
  const available = useMemo(
    () => (Array.isArray(workflows.data) ? workflows.data : (workflows.data?.results ?? [])),
    [workflows.data],
  );
  const [workflowId, setWorkflowId] = useState<string>('');

  useEffect(() => {
    if (!workflowId && available.length > 0) {
      const preferred = available.find((w) => w.key === 'litigation') ?? available[0];
      if (preferred) setWorkflowId(preferred.id);
    }
  }, [available, workflowId]);

  if (workflows.isLoading) return <SkeletonText lines={6} />;
  if (workflows.error) {
    return <ErrorState error={workflows.error} onRetry={() => void workflows.refetch()} />;
  }

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-ink-muted">
        Your clients follow their matter step by step in the client portal and app. Write what they
        read: a short title for each step, and the sentence they see while their matter is at that
        stage. Your team keeps its own stage names.
      </p>

      <Tabs
        tabs={available.map((w) => ({ key: w.id, label: w.name }))}
        value={workflowId}
        onChange={setWorkflowId}
        label="Matter workflows"
      />

      {workflowId && <WorkflowWording workflowId={workflowId} />}
    </div>
  );
}

function WorkflowWording({ workflowId }: { workflowId: string }) {
  const detail = useApiQuery<WorkflowDetail>(
    qk.detail('workflows', workflowId),
    STAFF.workflow(workflowId),
  );
  const [editing, setEditing] = useState<WorkflowStageWording | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const stages = useMemo(
    () => [...(detail.data?.stages ?? [])].sort((a, b) => a.position - b.position),
    [detail.data],
  );
  const steps = stages.filter((s) => !NOT_A_STEP.has(s.stage_type) && s.client_title);
  const others = stages.filter((s) => !steps.includes(s));

  // Preview a matter a few steps in, so "done" and "upcoming" both show.
  const previewStage =
    stages.find((s) => s.id === previewId) ?? steps[Math.min(3, steps.length - 1)] ?? null;

  if (detail.isLoading) return <SkeletonText lines={8} />;
  if (detail.error) {
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  }

  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="space-y-4">
        <StageList
          title="Steps in the tracker"
          description="In order. The client sees each one as done, in progress or still to come."
          stages={steps}
          numbered
          selectedId={previewStage?.id ?? null}
          onPreview={setPreviewId}
          onEdit={setEditing}
        />
        <StageList
          title="Pauses and endings"
          description="Not steps of their own. The tracker stays where the matter got to, and the client reads this sentence instead."
          stages={others}
          selectedId={previewStage?.id ?? null}
          onPreview={setPreviewId}
          onEdit={setEditing}
        />
      </div>

      <div className="order-first xl:sticky xl:top-4 xl:order-none">
        <ClientPreview steps={steps} stage={previewStage} />
      </div>

      <StageDialog stage={editing} workflowId={workflowId} onClose={() => setEditing(null)} />
    </div>
  );
}

function StageList({
  title,
  description,
  stages,
  numbered = false,
  selectedId,
  onPreview,
  onEdit,
}: {
  title: string;
  description: string;
  stages: WorkflowStageWording[];
  numbered?: boolean;
  selectedId: string | null;
  onPreview: (id: string) => void;
  onEdit: (stage: WorkflowStageWording) => void;
}) {
  if (stages.length === 0) return null;
  return (
    <Card>
      <CardHeader title={title} description={description} />
      <ul className="divide-y divide-line">
        {stages.map((stage, index) => (
          <li
            key={stage.id}
            className={cn(
              'flex items-start gap-3 px-4 py-3 sm:px-5',
              stage.id === selectedId && 'bg-canvas',
            )}
          >
            {numbered && (
              <span
                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hero text-xs font-semibold tabular-nums text-hero-ink"
                aria-hidden="true"
              >
                {index + 1}
              </span>
            )}
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              onClick={() => onPreview(stage.id)}
              aria-label={`Preview what the client sees at ${stage.client_title || stage.name}`}
            >
              <span className="block font-medium text-ink">
                {stage.client_title || <span className="text-ink-subtle">No title</span>}
              </span>
              <span className="mt-0.5 block text-sm text-ink-muted">{stage.client_label}</span>
              <span className="mt-1 block text-xs text-ink-subtle">
                Your team sees: {stage.name}
              </span>
            </button>
            <Can module="workflow" action="edit">
              <Button variant="ghost" size="sm" onClick={() => onEdit(stage)}>
                Edit
              </Button>
            </Can>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** The tracker as the client sees it, at the stage picked on the left. */
function ClientPreview({
  steps,
  stage,
}: {
  steps: WorkflowStageWording[];
  stage: WorkflowStageWording | null;
}) {
  const [anchor, setAnchor] = useState<string | null>(null);

  // A pause keeps the tracker where the matter got to: remember the last real step picked.
  useEffect(() => {
    if (stage && steps.some((s) => s.id === stage.id)) setAnchor(stage.id);
  }, [stage, steps]);

  if (!stage) return null;
  const cancelled = stage.stage_type === 'terminal_cancelled';
  const finishedByStage = stage.stage_type === 'terminal_success';
  const anchorIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === (steps.some((x) => x.id === stage.id) ? stage.id : anchor)),
  );
  const finished = finishedByStage || anchorIndex === steps.length - 1;

  const stateOf = (index: number): StepState =>
    finished || index < anchorIndex ? 'done' : index === anchorIndex ? 'current' : 'upcoming';

  return (
    <Card sensitivity="client-facing">
      <CardHeader title="What your client sees" description="Pick any stage to preview it." />
      <CardBody>
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Where your case is
        </p>
        <p className="mt-1 font-display text-lg leading-snug text-ink">{stage.client_label}</p>
        {cancelled ? (
          <p className="mt-4 text-sm text-ink-muted">A cancelled matter shows no tracker.</p>
        ) : (
          <ProgressSteps
            steps={steps.map((s, i) => ({ title: s.client_title, state: stateOf(i) }))}
          />
        )}
      </CardBody>
    </Card>
  );
}

function ProgressSteps({ steps }: { steps: { title: string; state: StepState }[] }) {
  return (
    <ol className="mt-4 space-y-0" aria-label="Progress">
      {steps.map((step, index) => (
        <li key={`${step.title}-${index}`} className="relative flex gap-3 pb-3 last:pb-0">
          {index < steps.length - 1 && (
            <span
              className={cn(
                'absolute left-[0.6875rem] top-6 h-[calc(100%-1.25rem)] w-0.5',
                step.state === 'done' ? 'bg-accent' : 'bg-line',
              )}
              aria-hidden="true"
            />
          )}
          <span
            className={cn(
              'relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[0.7rem] font-bold',
              step.state === 'done' && 'border-accent bg-accent text-white',
              step.state === 'current' &&
                'border-accent bg-surface text-accent ring-4 ring-accent/15',
              step.state === 'upcoming' && 'border-line bg-surface text-ink-subtle',
            )}
            aria-hidden="true"
          >
            {step.state === 'done' ? '✓' : index + 1}
          </span>
          <span
            className={cn(
              'pt-0.5 text-sm',
              step.state === 'current' ? 'font-semibold text-ink' : 'text-ink-muted',
              step.state === 'upcoming' && 'text-ink-subtle',
            )}
          >
            {step.title}
            <span className="sr-only">
              {step.state === 'done'
                ? ' (done)'
                : step.state === 'current'
                  ? ' (now)'
                  : ' (to come)'}
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function StageDialog({
  stage,
  workflowId,
  onClose,
}: {
  stage: WorkflowStageWording | null;
  workflowId: string;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();

  const form = useZodForm(schema, {
    values: { client_title: stage?.client_title ?? '', client_label: stage?.client_label ?? '' },
  });

  const save = useApiMutation<WorkflowStageWording, Values>(
    {
      mutationFn: (values) =>
        api.patch<WorkflowStageWording>(`${STAFF.workflowStage(stage!.id)}/`, values),
      invalidates: [qk.detail('workflows', workflowId)],
    },
    {
      onSuccess: (saved) => {
        toast({
          title: 'Client wording saved',
          description: `Clients now see “${saved.client_title}”.`,
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The change was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The wording could not be saved. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Modal
      open={stage !== null}
      onClose={onClose}
      title="Edit what the client sees"
      description={stage ? `Your team calls this stage “${stage.name}”.` : undefined}
    >
      <Form
        form={form}
        ariaLabel="Edit what the client sees"
        error={save.error}
        onSubmit={(values) => save.mutateAsync(values)}
      >
        <FormInput
          form={form}
          name="client_title"
          label="Step title"
          hint="The step's name in the client's progress tracker, e.g. “Filed with court”."
          maxLength={TITLE_MAX}
          autoFocus
          autoComplete="off"
        />
        <FormTextarea
          form={form}
          name="client_label"
          label="Message to the client"
          hint="What they read while their matter is at this stage. Plain words, no internal detail."
          rows={3}
          maxLength={SENTENCE_MAX}
        />
        <FormActions submitLabel="Save wording" busy={save.isPending} onCancel={onClose} />
      </Form>
    </Modal>
  );
}
